import type { ReactElement, ReactNode } from 'react'
import { useLoopsAllowed } from '@/components/brand/useLoopsAllowed'
import { EmptyState } from '@/components/play'
import type { EmptyStateTone } from '@/components/play/EmptyState'
import { cn } from '@/lib/utils'
import './watch.css'

/**
 * How a waiting glyph moves (watch.css, "Glyph motion"), each meaning something is still to come: `tune`, the signal's
 * arcs filling in while the viewer's connection tries; `lost`, the face lifting one step and settling while a lost room
 * tries again (the arcs brightening with it); `wake`, a camera's glyph waking (fading up) while its picture is on its
 * way; `hop`, the map's pin hopping while no camera is on the map; `wiggle`, once, the chat's bubble inviting the first
 * message. Glyphs for what isn't coming hold still.
 */
export type GlyphMotion = 'tune' | 'lost' | 'wake' | 'hop' | 'wiggle'

/** The motions that play once; every other one loops. */
const ONCE: ReadonlySet<GlyphMotion> = new Set(['wiggle'])

/**
 * A glyph's motion while it may run, for its `data-motion`: nothing under reduced motion, and no loop while the page is
 * hidden (it picks up again when the page is back).
 */
export function useGlyphMotion<T extends GlyphMotion>(motion: T | undefined): T | undefined {
  const allowed = useLoopsAllowed()
  if (motion === undefined || !allowed.motion) return undefined
  return ONCE.has(motion) || allowed.loops ? motion : undefined
}

/**
 * The brand's stacked disc, every wait's glyph on the public watch screen: a white face with the ink outline, offset up
 * and right from a pink layer and a yellow one, as the stacked play mark (green stays for live). The canvas's waits, the
 * chat with no messages, the map with nothing to frame, the swap card and the phone's empty tabs all use it, so a wait
 * reads as StreamBridge waiting, not as a control: the yellow is a layer under a white face, never a button on its own.
 * `motion` moves it (not under reduced motion, no loop while the page is hidden); leave it out to hold it still, as for
 * what isn't coming. Decorative: any words go beside it, on screen or visually hidden.
 */
export function StackedGlyph({ icon, motion, className }: { icon: ReactElement; motion?: GlyphMotion; className?: string }) {
  const moving = useGlyphMotion(motion)
  return (
    <span className={cn('watch-stack', className)} data-motion={moving} aria-hidden="true">
      <span className="watch-stack__face">{icon}</span>
    </span>
  )
}

/**
 * A state's glyph in Play EmptyState's neutral disc, still and wordless: what has gone wrong rather than what is on its
 * way (the video refused, a dropped camera in the swap card, a picture that won't draw). In the tone of what it sits on
 * (watch.css). Decorative: any words go beside it.
 */
export function StateGlyph({ icon, className }: { icon: ReactElement; className?: string }) {
  return (
    <span className={cn('watch-glyph', className)} aria-hidden="true">
      {icon}
    </span>
  )
}

/**
 * A wait's art over its heading: the stacked disc (or the brand mark) above a compact Play EmptyState's title, an h3,
 * with the space-s step between them and none above or below (the wrapper places it). A figurative title leads with its
 * plain meaning for screen readers (`plain`, visually hidden at the heading's start), so heading navigation reads the
 * fact first. `role="status"` where it replaces something that was there (the canvas); not inside a live log.
 */
export function WaitingState({
  art,
  title,
  plain,
  tone = 'default',
  role,
  className,
}: {
  art: ReactNode
  title: string
  plain?: string
  tone?: EmptyStateTone
  role?: 'status'
  className?: string
}) {
  return (
    <div className={cn('watch-wait', className)} role={role}>
      {art}
      <EmptyState
        size="compact"
        tone={tone}
        headingLevel={3}
        title={
          plain ? (
            <>
              <span className="sr-only">{plain}. </span>
              {title}
            </>
          ) : (
            title
          )
        }
      />
    </div>
  )
}
